import React, { useEffect, useState, useRef } from "react";
import { api } from "../context/AuthContext";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { Storefront, Plus, PencilSimple, Trash, UploadSimple, Package, ChartBar, FileXls, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

function ProductDialog({ editing, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const blank = () => ({ name: editing?.name || "", description: editing?.description || "", price: editing ? String(editing.price) : "", currency: editing?.currency || "DOP", active: editing ? editing.active : true, image_url: editing?.image_url || "", stock: (editing && editing.stock !== null && editing.stock !== undefined) ? String(editing.stock) : "", low_stock_threshold: (editing && editing.low_stock_threshold !== null && editing.low_stock_threshold !== undefined) ? String(editing.low_stock_threshold) : "" });
  const [f, setF] = useState(blank());
  const [file, setFile] = useState(null);
  useEffect(() => { if (open) { setF(blank()); setFile(null); } }, [open]); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!f.price || Number(f.price) <= 0) { toast.error("Indica un precio válido"); return; }
    setBusy(true);
    try {
      const payload = { name: f.name.trim(), description: f.description, price: Number(f.price), currency: f.currency, active: f.active, image_url: f.image_url, stock: f.stock === "" ? null : Math.max(0, parseInt(f.stock, 10) || 0), low_stock_threshold: f.low_stock_threshold === "" ? null : Math.max(0, parseInt(f.low_stock_threshold, 10) || 0) };
      let prod;
      if (editing) { const { data } = await api.put(`/admin/products/${editing.id}`, payload); prod = data; }
      else { const { data } = await api.post(`/admin/products`, payload); prod = data; }
      if (file) { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/products/${prod.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const preview = file ? URL.createObjectURL(file) : imgSrc(f.image_url);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button data-testid={`edit-product-${editing.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title="Editar"><PencilSimple size={15} weight="bold" /></Button>
        ) : (
          <Button data-testid="add-product-btn" className="bg-emerald-800 hover:bg-emerald-900 gap-1.5"><Plus size={16} weight="bold" /> Nuevo producto</Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="product-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          <DialogDescription className="sr-only">Producto</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre</Label><Input data-testid="product-name" className="mt-1 h-11" value={f.name} onChange={set("name")} /></div>
          <div><Label>Descripción</Label><Textarea data-testid="product-description" className="mt-1" rows={2} value={f.description} onChange={set("description")} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Precio</Label><Input data-testid="product-price" type="number" step="0.01" min="0" className="mt-1 h-11 font-mono-num" value={f.price} onChange={set("price")} /></div>
            <div>
              <Label>Moneda</Label>
              <select data-testid="product-currency" className="mt-1 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm" value={f.currency} onChange={set("currency")}>
                <option value="DOP">DOP</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div><Label>Stock</Label><Input data-testid="product-stock" type="number" min="0" placeholder="∞" className="mt-1 h-11 font-mono-num" value={f.stock} onChange={set("stock")} /></div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Deja el stock vacío para inventario ilimitado.</p>
          <div><Label>Alerta de stock bajo (mínimo)</Label><Input data-testid="product-low-stock" type="number" min="0" placeholder="Ej.: 5 (vacío = sin alerta)" className="mt-1 h-11 font-mono-num" value={f.low_stock_threshold} onChange={set("low_stock_threshold")} /></div>
          <div>
            <Label>Imagen</Label>
            <div className="mt-1 flex items-center gap-3">
              {preview && <img src={preview} alt="" className="h-16 w-16 rounded-lg object-cover border border-emerald-100" />}
              <Button type="button" data-testid="product-image-btn" size="sm" variant="outline" className="gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => fileRef.current?.click()}><UploadSimple size={14} weight="bold" /> Subir imagen</Button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="product-image-file" />
            </div>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700">
            <Switch data-testid="product-active" checked={f.active} onCheckedChange={(v) => setF((p) => ({ ...p, active: v }))} /> Activo (visible para socios)
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="product-save" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestockDialog({ product, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const n = parseInt(qty, 10);
    if (!n || n <= 0) { toast.error("Indica una cantidad válida"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/products/${product.id}/restock`, { qty: n });
      toast.success(`Añadidas ${n} uds al stock`);
      setOpen(false); setQty(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setQty(""); }}>
      <DialogTrigger asChild>
        <Button data-testid={`restock-product-${product.id}`} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" title="Reabastecer stock"><Plus size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="restock-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">Reabastecer — {product.name}</DialogTitle>
          <DialogDescription>Stock actual: {product.stock === null || product.stock === undefined ? "∞" : product.stock}</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Cantidad a sumar</Label>
          <Input data-testid="restock-qty" type="number" min="1" className="mt-1 h-11 font-mono-num" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} autoFocus />
        </div>
        <DialogFooter>
          <Button data-testid="restock-confirm" onClick={submit} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900">{busy ? "Guardando..." : "Añadir stock"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("¿Eliminar este producto?")) return; try { await api.delete(`/admin/products/${id}`); toast.success("Producto eliminado"); load(); } catch (e) { toast.error("Error"); } };
  return (
    <>
      <div className="flex justify-end mb-4"><ProductDialog onDone={load} /></div>
      {loading ? <p className="text-slate-500 py-6">Cargando...</p> : items.length === 0 ? (
        <p className="text-slate-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="store-admin-empty">Aún no hay productos. Crea el primero con “Nuevo producto”.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-admin-list">
          {items.map((p) => (
            <div key={p.id} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm" data-testid={`admin-product-${p.id}`}>
              <div className="h-36 bg-slate-50 flex items-center justify-center overflow-hidden">
                {imgSrc(p.image_url) ? <img src={imgSrc(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <Storefront size={40} weight="duotone" className="text-slate-300" />}
              </div>
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-emerald-900">{p.name}</p>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Activo" : "Oculto"}</span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 mt-0.5 min-h-[2rem]">{p.description}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="font-mono-num font-bold text-emerald-900">{money(p.price, p.currency)}</p>
                  <span className="text-[11px] text-slate-500" data-testid={`admin-stock-${p.id}`}>{p.stock === null || p.stock === undefined ? "Stock ∞" : `Stock: ${p.stock}`}</span>
                </div>
                {p.stock !== null && p.stock !== undefined && p.low_stock_threshold !== null && p.low_stock_threshold !== undefined && p.stock <= p.low_stock_threshold && (
                  <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`low-stock-${p.id}`}>⚠ Stock bajo</span>
                )}
                <div className="flex gap-1.5 mt-3">
                  <ProductDialog editing={p} onDone={load} />
                  {p.stock !== null && p.stock !== undefined && <RestockDialog product={p} onDone={load} />}
                  <Button data-testid={`delete-product-${p.id}`} size="sm" variant="outline" onClick={() => remove(p.id)} className="border-red-200 text-red-700 hover:bg-red-50" title="Eliminar"><Trash size={15} weight="bold" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [awaitingCount, setAwaitingCount] = useState(0);
  const load = () => {
    api.get(`/admin/store-orders?status=awaiting`).then((r) => setAwaitingCount(r.data.length)).catch(() => {});
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (branch) params.set("branch", branch);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    const qs = params.toString();
    return api.get(`/admin/store-orders${qs ? "?" + qs : ""}`).then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/admin/branches").then((r) => setBranches(r.data.branches || [])).catch(() => {}); }, []);
  useEffect(() => { setLoading(true); load(); }, [status, branch, start, end]); // eslint-disable-line
  const mark = async (id, st) => { try { await api.put(`/admin/store-orders/${id}/status`, { status: st }); toast.success(st === "delivered" ? "Marcado como entregado" : "Marcado como pagado"); load(); } catch (e) { toast.error("Error"); } };
  const confirmPay = async (id) => { try { const { data } = await api.post(`/admin/store-orders/${id}/confirm-payment`); toast.success(data.ticket ? `Pago confirmado · boleto ${data.ticket.code}` : "Pago confirmado"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const cancelOrder = async (id) => { if (!window.confirm("¿Cancelar este pedido y restaurar el stock?")) return; try { await api.post(`/admin/store-orders/${id}/cancel`); toast.success("Pedido cancelado"); load(); } catch (e) { toast.error("Error"); } };
  return (
    <div data-testid="store-orders-admin">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select data-testid="orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm">
          <option value="">Todos los estados</option>
          <option value="awaiting">Pendientes de pago (Cash App){awaitingCount ? ` · ${awaitingCount}` : ""}</option>
          <option value="paid">Pendientes de entregar</option>
          <option value="delivered">Entregados</option>
        </select>
        <select data-testid="orders-branch-filter" value={branch} onChange={(e) => setBranch(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm">
          <option value="">Todas las sucursales</option>
          {branches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <label className="text-xs font-semibold text-slate-600 ml-1">Desde</label>
        <input type="date" data-testid="orders-start" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm" />
        <label className="text-xs font-semibold text-slate-600">Hasta</label>
        <input type="date" data-testid="orders-end" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm" />
        {(start || end) && <button data-testid="orders-clear" onClick={() => { setStart(""); setEnd(""); }} className="text-xs font-semibold text-emerald-700 hover:underline">Limpiar</button>}
        {awaitingCount > 0 && <button data-testid="orders-awaiting-chip" onClick={() => setStatus("awaiting")} className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full hover:bg-amber-200">{awaitingCount} pendiente(s) de pago</button>}
      </div>
      {loading ? <p className="text-slate-500 py-6">Cargando...</p> : orders.length === 0 ? (
        <p className="text-slate-400 py-10 text-center border border-dashed border-gray-200 rounded-lg" data-testid="store-orders-empty">No hay pedidos con esos filtros.</p>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-400 border-b border-gray-100"><th className="px-3 py-2 font-medium">Fecha</th><th className="px-3 py-2 font-medium">Socio</th><th className="px-3 py-2 font-medium">Producto</th><th className="px-3 py-2 font-medium text-right">Monto</th><th className="px-3 py-2 font-medium">Boleto</th><th className="px-3 py-2 font-medium">Estado</th><th className="px-3 py-2 font-medium"></th></tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-gray-50 last:border-0" data-testid={`admin-order-${o.id}`}>
              <td className="px-3 py-2 text-slate-600">{(o.created_at || "").slice(0, 10)}</td>
              <td className="px-3 py-2 text-slate-700">{o.member_name}</td>
              <td className="px-3 py-2 text-emerald-900 font-semibold">{o.product_name}</td>
              <td className="px-3 py-2 text-right font-mono-num">{money(o.price, o.currency)}</td>
              <td className="px-3 py-2">{o.ticket_code ? <span className="text-[11px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{o.ticket_code}</span> : <span className="text-slate-400 text-xs">—</span>}</td>
              <td className="px-3 py-2">{o.status === "awaiting_payment" ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Cash App · pendiente{o.pay_ref ? ` #${o.pay_ref}` : ""}</span> : <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${o.status === "delivered" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{o.status === "delivered" ? "Entregado" : "Pagado"}</span>}</td>
              <td className="px-3 py-2 text-right">
                {o.status === "awaiting_payment" ? (
                  <div className="flex gap-1.5 justify-end">
                    <Button data-testid={`order-confirm-pay-${o.id}`} size="sm" className="text-xs bg-emerald-800 hover:bg-emerald-900 gap-1" onClick={() => confirmPay(o.id)}><CheckCircle size={13} weight="bold" /> Confirmar pago</Button>
                    <Button data-testid={`order-cancel-${o.id}`} size="sm" variant="outline" className="text-xs border-red-200 text-red-600" onClick={() => cancelOrder(o.id)}>Cancelar</Button>
                  </div>
                ) : o.status === "delivered" ? (
                  <Button data-testid={`order-mark-paid-${o.id}`} size="sm" variant="outline" className="text-xs border-slate-200 text-slate-600" onClick={() => mark(o.id, "paid")}>Revertir</Button>
                ) : (
                  <Button data-testid={`order-deliver-${o.id}`} size="sm" className="text-xs bg-emerald-800 hover:bg-emerald-900 gap-1" onClick={() => mark(o.id, "delivered")}><CheckCircle size={13} weight="bold" /> Entregar</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      )}
    </div>
  );
}

function SalesView() {
  const [sales, setSales] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const qp = () => { const p = new URLSearchParams(); if (start) p.set("start", start); if (end) p.set("end", end); const q = p.toString(); return q ? "?" + q : ""; };
  useEffect(() => { api.get(`/admin/store/sales${qp()}`).then((r) => setSales(r.data)).catch(() => {}); }, [start, end]); // eslint-disable-line
  const exportXlsx = async () => {
    try {
      const res = await api.get(`/admin/store/sales.xlsx${qp()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = "ventas_tienda.xlsx"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar"); }
  };
  if (!sales) return <p className="text-slate-500 py-6">Cargando...</p>;
  return (
    <div data-testid="store-sales">
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="sales-date-filter">
        <label className="text-xs font-semibold text-slate-600">Desde</label>
        <input type="date" data-testid="sales-start" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm" />
        <label className="text-xs font-semibold text-slate-600">Hasta</label>
        <input type="date" data-testid="sales-end" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm" />
        {(start || end) && <button data-testid="sales-clear" onClick={() => { setStart(""); setEnd(""); }} className="text-xs font-semibold text-emerald-700 hover:underline">Limpiar</button>}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-2 flex-wrap text-sm">
          <span className="bg-emerald-50 text-emerald-800 font-semibold px-3 py-1.5 rounded-lg">Pedidos: {sales.total_orders}</span>
          <span className="bg-amber-50 text-amber-800 font-semibold px-3 py-1.5 rounded-lg">Boletos: {sales.total_tickets}</span>
          {Object.entries(sales.totals_by_currency || {}).map(([c, v]) => (
            <span key={c} className="bg-slate-50 text-slate-700 font-semibold px-3 py-1.5 rounded-lg font-mono-num">{money(v, c)}</span>
          ))}
        </div>
        <Button data-testid="sales-export-excel" onClick={exportXlsx} className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"><FileXls size={16} weight="bold" /> Exportar Excel</Button>
      </div>
      {sales.by_product.length === 0 ? (
        <p className="text-slate-400 py-10 text-center border border-dashed border-gray-200 rounded-lg">Aún no hay ventas.</p>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-5" data-testid="top-products-chart">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={18} weight="duotone" className="text-emerald-700" />
              <h4 className="font-semibold text-emerald-900 text-sm">Top productos más vendidos</h4>
            </div>
            {(() => {
              const top = [...sales.by_product].sort((a, b) => b.count - a.count).slice(0, 8);
              const max = Math.max(1, ...top.map((r) => r.count));
              return (
                <div className="space-y-2.5">
                  {top.map((r, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`top-product-bar-${i}`}>
                      <span className="w-32 shrink-0 text-xs font-semibold text-emerald-900 truncate text-right" title={r.product_name}>{r.product_name}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                        <div className="h-full bg-emerald-600 rounded-full transition-all flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (r.count / max) * 100)}%` }}>
                          <span className="text-[11px] font-bold text-white">{r.count}</span>
                        </div>
                      </div>
                      <span className="w-24 shrink-0 text-xs font-mono-num text-slate-500 text-right">{money(r.total, r.currency)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-gray-100"><th className="px-3 py-2 font-medium">Producto</th><th className="px-3 py-2 font-medium text-right">Ventas</th><th className="px-3 py-2 font-medium text-right">Monto</th><th className="px-3 py-2 font-medium text-right">Boletos</th></tr></thead>
              <tbody>
                {sales.by_product.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0" data-testid={`sales-row-${i}`}>
                    <td className="px-3 py-2 text-emerald-900 font-semibold">{r.product_name}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{r.count}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{money(r.total, r.currency)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{r.tickets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function StoreAdminPanel() {
  const [view, setView] = useState("products");
  const tabs = [
    { key: "products", label: "Productos", icon: Storefront },
    { key: "orders", label: "Pedidos", icon: Package },
    { key: "sales", label: "Ventas", icon: ChartBar },
  ];
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="store-admin-panel">
      <div className="flex items-center gap-2 mb-4">
        <Storefront size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">Tienda Digital</h3>
      </div>
      <div className="flex gap-1.5 mb-5 border-b border-gray-100">
        {tabs.map((tb) => (
          <button key={tb.key} data-testid={`store-tab-${tb.key}`} onClick={() => setView(tb.key)} className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === tb.key ? "border-emerald-700 text-emerald-800" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            <tb.icon size={16} weight="bold" /> {tb.label}
          </button>
        ))}
      </div>
      {view === "products" && <ProductsView />}
      {view === "orders" && <OrdersView />}
      {view === "sales" && <SalesView />}
    </div>
  );
}
